/**
 * Desktop Notification Service
 *
 * Generyczna klasa do obsługi powiadomień desktop w aplikacji.
 * Zapewnia ujednolicony interfejs do tworzenia, konfiguracji i zarządzania powiadomieniami desktop.
 *
 * @example
 * // Podstawowe użycie
 * const notificationService = DesktopNotificationService.getInstance()
 *
 * // Sprawdź uprawnienia
 * const hasPermission = await notificationService.hasPermission()
 *
 * // Poproś o uprawnienia jeśli potrzebne
 * if (!hasPermission) {
 *   const permission = await notificationService.requestPermission()
 * }
 *
 * // Wyświetl powiadomienie
 * const notification = notificationService.show({
 *   title: 'Nowa wiadomość',
 *   body: 'Otrzymałeś nową wiadomość od Jana Kowalskiego',
 *   icon: '/path/to/icon.png',
 *   onClick: () => {
 *     // Akcja po kliknięciu
 *     window.focus()
 *     // Przejdź do konkretnej sekcji aplikacji
 *   }
 * })
 *
 * @example
 * // Powiadomienie z zaawansowanymi opcjami
 * notificationService.show({
 *   title: 'Powiadomienie systemowe',
 *   body: 'Opis powiadomienia',
 *   icon: '/icon.png',
 *   image: '/preview.jpg',
 *   badge: '/badge.png',
 *   tag: 'message-123', // Grupowanie powiadomień
 *   requireInteraction: true, // Nie znika automatycznie
 *   silent: false,
 *   vibrate: [200, 100, 200],
 *   actions: [
 *     { action: 'reply', title: 'Odpowiedz', icon: '/reply-icon.png' },
 *     { action: 'archive', title: 'Archiwizuj', icon: '/archive-icon.png' }
 *   ],
 *   data: { conversationId: 123, userId: 456 },
 *   onClick: (event) => handleClick(event),
 *   onError: (error) => handleError(error),
 *   onShow: () => console.log('Powiadomienie wyświetlone'),
 *   onClose: () => console.log('Powiadomienie zamknięte'),
 *   autoClose: 5000 // Automatyczne zamknięcie po 5 sekundach
 * })
 */

class DesktopNotificationService {
  constructor() {
    this.activeNotifications = new Map()
    this.defaultOptions = {
      dir: 'auto',
      lang: 'pl',
      requireInteraction: false,
      silent: false,
      autoClose: 5000 // Domyślnie 5 sekund
    }
  }

  /**
   * Sprawdza czy powiadomienia desktop są wspierane przez przeglądarkę
   * @returns {boolean}
   */
  isSupported() {
    return 'Notification' in window
  }

  /**
   * Sprawdza czy aplikacja ma uprawnienia do wyświetlania powiadomień
   * @returns {boolean}
   */
  hasPermission() {
    if (!this.isSupported()) {
      return false
    }
    return Notification.permission === 'granted'
  }

  /**
   * Zwraca aktualny status uprawnień
   * @returns {'granted' | 'denied' | 'default' | 'not-supported'}
   */
  getPermissionStatus() {
    if (!this.isSupported()) {
      return 'not-supported'
    }
    return Notification.permission
  }

  /**
   * Prosi użytkownika o uprawnienia do wyświetlania powiadomień
   * @returns {Promise<'granted' | 'denied' | 'default' | 'not-supported'>}
   */
  async requestPermission() {
    if (!this.isSupported()) {
      return 'not-supported'
    }

    if (Notification.permission === 'granted') {
      return 'granted'
    }

    if (Notification.permission === 'denied') {
      return 'denied'
    }

    try {
      const permission = await Notification.requestPermission()
      return permission
    } catch (error) {
      console.error(
        'Błąd podczas proszenia o uprawnienia do powiadomień:',
        error
      )
      return 'denied'
    }
  }

  /**
   * Wyświetla powiadomienie desktop
   * @param {Object} options - Opcje powiadomienia
   * @param {string} options.title - Tytuł powiadomienia
   * @param {string} [options.body] - Treść powiadomienia
   * @param {string} [options.icon] - URL do ikony
   * @param {string} [options.image] - URL do obrazu
   * @param {string} [options.badge] - URL do badge'a
   * @param {string} [options.tag] - Tag do grupowania powiadomień
   * @param {boolean} [options.requireInteraction] - Czy powiadomienie wymaga interakcji
   * @param {boolean} [options.silent] - Czy powiadomienie ma być ciche
   * @param {string} [options.dir] - Kierunek tekstu ('auto', 'ltr', 'rtl')
   * @param {string} [options.lang] - Język powiadomienia
   * @param {number[]} [options.vibrate] - Wzór wibracji
   * @param {Array} [options.actions] - Akcje dostępne w powiadomieniu
   * @param {Object} [options.data] - Dane dodatkowe
   * @param {Function} [options.onClick] - Callback wywoływany po kliknięciu
   * @param {Function} [options.onError] - Callback wywoływany przy błędzie
   * @param {Function} [options.onShow] - Callback wywoływany po wyświetleniu
   * @param {Function} [options.onClose] - Callback wywoływany po zamknięciu
   * @param {number} [options.autoClose] - Czas po którym powiadomienie zostanie automatycznie zamknięte (w ms)
   * @returns {Notification|null} - Obiekt powiadomienia lub null jeśli nie można utworzyć
   */
  show(options) {
    if (!this.hasPermission()) {
      console.warn('Brak uprawnień do wyświetlania powiadomień desktop')
      return null
    }

    const {
      title,
      body,
      icon,
      image,
      badge,
      tag,
      requireInteraction,
      silent,
      dir,
      lang,
      vibrate,
      actions,
      data,
      onClick,
      onError,
      onShow,
      onClose,
      autoClose,
      ...otherOptions
    } = options

    if (!title) {
      console.error('Tytuł powiadomienia jest wymagany')
      return null
    }

    try {
      // Przygotowanie opcji powiadomienia
      const notificationOptions = {
        ...this.defaultOptions,
        body,
        icon,
        requireInteraction,
        silent,
        dir: dir || this.defaultOptions.dir,
        lang: lang || this.defaultOptions.lang,
        ...otherOptions
      }

      // Dodaj opcjonalne pola tylko jeśli są określone
      if (image) notificationOptions.image = image
      if (badge) notificationOptions.badge = badge
      if (tag) notificationOptions.tag = tag
      if (vibrate) notificationOptions.vibrate = vibrate
      if (actions && actions.length > 0) notificationOptions.actions = actions
      if (data) notificationOptions.data = data

      // Utworzenie powiadomienia
      const notification = new Notification(title, notificationOptions)

      // Generowanie unikalnego ID dla powiadomienia
      const notificationId = this._generateNotificationId()
      this.activeNotifications.set(notificationId, notification)

      // Obsługa eventów
      if (onClick) {
        notification.onclick = event => {
          try {
            onClick(event, notification)
          } catch (error) {
            console.error('Błąd w obsłudze onClick powiadomienia:', error)
          }
        }
      }

      if (onError) {
        notification.onerror = event => {
          try {
            onError(event, notification)
          } catch (error) {
            console.error('Błąd w obsłudze onError powiadomienia:', error)
          }
        }
      }

      if (onShow) {
        notification.onshow = event => {
          try {
            onShow(event, notification)
          } catch (error) {
            console.error('Błąd w obsłudze onShow powiadomienia:', error)
          }
        }
      }

      // Obsługa zamknięcia z cleanup
      notification.onclose = event => {
        this.activeNotifications.delete(notificationId)
        if (onClose) {
          try {
            onClose(event, notification)
          } catch (error) {
            console.error('Błąd w obsłudze onClose powiadomienia:', error)
          }
        }
      }

      // Automatyczne zamknięcie jeśli określone
      const closeTimeout =
        autoClose !== undefined ? autoClose : this.defaultOptions.autoClose
      if (closeTimeout > 0) {
        setTimeout(() => {
          if (this.activeNotifications.has(notificationId)) {
            notification.close()
          }
        }, closeTimeout)
      }

      return notification
    } catch (error) {
      console.error('Błąd podczas tworzenia powiadomienia desktop:', error)
      if (onError) {
        onError(error, null)
      }
      return null
    }
  }

  /**
   * Zamyka wszystkie aktywne powiadomienia
   */
  closeAll() {
    this.activeNotifications.forEach(notification => {
      try {
        notification.close()
      } catch (error) {
        console.error('Błąd podczas zamykania powiadomienia:', error)
      }
    })
    this.activeNotifications.clear()
  }

  /**
   * Zamyka powiadomienia z określonym tagiem
   * @param {string} tag - Tag powiadomień do zamknięcia
   */
  closeByTag(tag) {
    this.activeNotifications.forEach((notification, id) => {
      if (notification.tag === tag) {
        try {
          notification.close()
        } catch (error) {
          console.error('Błąd podczas zamykania powiadomienia:', error)
        }
      }
    })
  }

  /**
   * Zwraca liczbę aktywnych powiadomień
   * @returns {number}
   */
  getActiveCount() {
    return this.activeNotifications.size
  }

  /**
   * Ustawia domyślne opcje dla wszystkich powiadomień
   * @param {Object} options - Nowe domyślne opcje
   */
  setDefaultOptions(options) {
    this.defaultOptions = { ...this.defaultOptions, ...options }
  }

  /**
   * Resetuje domyślne opcje do wartości początkowych
   */
  resetDefaultOptions() {
    this.defaultOptions = {
      dir: 'auto',
      lang: 'pl',
      requireInteraction: false,
      silent: false,
      autoClose: 5000
    }
  }

  /**
   * Generuje unikalny ID dla powiadomienia
   * @private
   * @returns {string}
   */
  _generateNotificationId() {
    return `notification_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`
  }

  /**
   * Czyści zasoby serwisu
   */
  destroy() {
    this.closeAll()
  }

  /**
   * Zwraca singleton instance
   * @returns {DesktopNotificationService}
   */
  static getInstance() {
    if (!DesktopNotificationService.instance) {
      DesktopNotificationService.instance = new DesktopNotificationService()
    }
    return DesktopNotificationService.instance
  }
}

export default DesktopNotificationService
